import axios from "axios";
import { cacheGet, cacheSet, queueAdd, queueGetAll, queueDelete } from "@/lib/offlineDb";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("rapportini_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Iter 44: refresh automatico del token su 401 (una volta per richiesta).
// Evita la "sessione fantasma": prima, un token scaduto faceva fallire le
// chiamate in silenzio e l'utente vedeva liste vuote / zero ore.
let refreshPromise = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    const url = cfg.url || "";
    if (status === 401 && !cfg._retried && !url.startsWith("/auth/")) {
      cfg._retried = true;
      try {
        refreshPromise =
          refreshPromise ||
          axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        await refreshPromise;
        refreshPromise = null;
        // il nuovo access token è nel cookie (che il backend legge per primo)
        return api.request(cfg);
      } catch (e2) {
        refreshPromise = null;
        // refresh impossibile: sessione davvero scaduta → login pulito
        localStorage.removeItem("rapportini_token");
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

const CACHE_PATHS = [
  /^\/auth\/me$/,
  /^\/employees$/,
  /^\/commesse$/,
  /^\/settings$/,
  /^\/rapportini(\?|$)/,
  /^\/rapportini\/[^/]+$/,
  /^\/rapportini\/totals\/by-commessa(\?|$)/,
];

function shouldCache(method, url) {
  if (method !== "get") return false;
  return CACHE_PATHS.some((re) => re.test(url));
}

function buildCacheKey(method, url, params) {
  const qs = params ? new URLSearchParams(params).toString() : "";
  return `${method.toUpperCase()} ${url}${qs ? "?" + qs : ""}`;
}

// ---- Read with offline fallback ----
const originalGet = api.get.bind(api);
api.get = async (url, config = {}) => {
  const key = buildCacheKey("get", url, config.params);
  if (shouldCache("get", url)) {
    try {
      const res = await originalGet(url, config);
      cacheSet(key, res.data);
      return res;
    } catch (e) {
      // Iter 44: la cache offline è un fallback SOLO per problemi di rete
      // (offline/timeout). Gli errori del server (401/403/500) devono
      // emergere, altrimenti l'utente vede dati vecchi o "zero ore".
      const isNetworkProblem =
        !e.response &&
        (e.code === "ERR_NETWORK" ||
          e.code === "ECONNABORTED" ||
          e.message === "Network Error" ||
          !navigator.onLine);
      if (isNetworkProblem) {
        const cached = await cacheGet(key);
        if (cached !== undefined && cached !== null) {
          return { data: cached, cached: true, status: 200 };
        }
      }
      throw e;
    }
  }
  return originalGet(url, config);
};

// ---- Write with offline queue (only for rapportini) ----
const QUEUEABLE_PATHS = [/^\/rapportini(\/[^/]+)?$/];

function isQueueable(method, url) {
  if (!["post", "put", "delete"].includes(method)) return false;
  return QUEUEABLE_PATHS.some((re) => re.test(url));
}

const originalPost = api.post.bind(api);
const originalPut = api.put.bind(api);
const originalDelete = api.delete.bind(api);

async function tryOnlineOrQueue(method, url, data, config) {
  const fn =
    method === "post" ? originalPost : method === "put" ? originalPut : originalDelete;
  try {
    const res =
      method === "delete"
        ? await fn(url, config)
        : await fn(url, data, config);
    return res;
  } catch (e) {
    const isNetwork =
      !e.response && (e.code === "ERR_NETWORK" || e.message === "Network Error" || !navigator.onLine);
    if (isNetwork && isQueueable(method, url)) {
      // Queue the request
      const tempId = `temp-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
      await queueAdd({
        method,
        url,
        data: data || null,
        token: localStorage.getItem("rapportini_token") || null,
        temp_id: tempId,
      });
      // Return a fake response so the UI does not crash
      const fakeData =
        method === "post"
          ? { ...data, id: tempId, _queued: true }
          : method === "put"
          ? { ...(data || {}), id: tempId, _queued: true }
          : { ok: true, _queued: true };
      return { data: fakeData, queued: true, status: 202 };
    }
    throw e;
  }
}

api.post = (url, data, config) => tryOnlineOrQueue("post", url, data, config);
api.put = (url, data, config) => tryOnlineOrQueue("put", url, data, config);
api.delete = (url, config) => tryOnlineOrQueue("delete", url, null, config);

// ---- Replay queue ----
export async function replayQueue(onProgress) {
  const items = await queueGetAll();
  let successes = 0;
  let failures = 0;
  for (const item of items) {
    try {
      const fn =
        item.method === "post"
          ? originalPost
          : item.method === "put"
          ? originalPut
          : originalDelete;
      if (item.method === "delete") {
        await fn(item.url);
      } else {
        await fn(item.url, item.data);
      }
      await queueDelete(item.id);
      successes++;
    } catch (e) {
      // If validation error (4xx not 401), drop the item to avoid endless retries.
      const status = e?.response?.status;
      if (status && status >= 400 && status < 500 && status !== 401) {
        await queueDelete(item.id);
        failures++;
      } else {
        // Keep the item, will retry next time
        failures++;
      }
    }
    if (onProgress) onProgress({ done: successes, total: items.length, failures });
  }
  return { successes, failures, total: items.length };
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
